// @ts-nocheck
/**
 * bindingContract.ts — V2 display vs persist binding rules (DEC-036).
 *
 * Display authority: typed ValueSource on component fields (e.g. text.source).
 * binding.read: persist ref for editable write-back only (same ref as source.ref).
 */

import type { ComponentNode } from '../../types/component';
import type { DataRef, ValueSource } from '../../types/binding';
import type { CompiledRuntimeNode } from '../compiler';

const VALUE_SOURCE_KINDS = new Set(['static', 'binding', 'expression']);

/** True when value looks like a canonical ValueSource. */
export function isValueSourceShape(value: unknown): value is ValueSource<unknown> {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const kind = (value as { kind?: unknown }).kind;
  return typeof kind === 'string' && VALUE_SOURCE_KINDS.has(kind);
}

/**
 * Unwrap mistaken `{ source: ValueSource }` nesting (legacy editor export).
 * Does not treat canonical ValueSource as nested.
 */
export function unwrapValueSource<T>(raw: unknown): ValueSource<T> | undefined {
  if (isValueSourceShape(raw)) {
    return raw as ValueSource<T>;
  }
  if (raw !== null && typeof raw === 'object') {
    const nested = (raw as { source?: unknown }).source;
    if (isValueSourceShape(nested)) {
      return nested as ValueSource<T>;
    }
  }
  return undefined;
}

export function bindingRefFromValueSource(
  source: ValueSource<unknown> | undefined,
): DataRef | undefined {
  if (source?.kind === 'binding' && source.ref && typeof source.ref === 'object' && 'kind' in source.ref) {
    return source.ref as DataRef;
  }
  return undefined;
}

/** Stable JSON key order for comparing DataRef objects. */
export function dataRefsEqual(a: DataRef, b: DataRef): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function typedBlock(
  node: ComponentNode | CompiledRuntimeNode,
): Record<string, unknown> | undefined {
  const props = 'props' in node && node.props != null
    ? (node.props as Record<string, unknown>)
    : (node as ComponentNode as unknown as Record<string, unknown>);
  const type = node.type;
  switch (type) {
    case 'text':
      return props.text as Record<string, unknown> | undefined;
    case 'number':
      return props.number as Record<string, unknown> | undefined;
    case 'boolean':
      return props.boolean as Record<string, unknown> | undefined;
    case 'date':
      return props.date as Record<string, unknown> | undefined;
    case 'enum':
      return props.enum as Record<string, unknown> | undefined;
    case 'stringSelect':
      return props.stringSelect as Record<string, unknown> | undefined;
    case 'image':
      return props.image as Record<string, unknown> | undefined;
    case 'html':
      return props.html as Record<string, unknown> | undefined;
    case 'qrCode':
      return props.qrCode as Record<string, unknown> | undefined;
    case 'rating':
      return props.rating as Record<string, unknown> | undefined;
    case 'slider':
      return props.slider as Record<string, unknown> | undefined;
    case 'color':
      return props.color as Record<string, unknown> | undefined;
    case 'table':
      return props.table as Record<string, unknown> | undefined;
    case 'listView':
      return props.listView as Record<string, unknown> | undefined;
    case 'button':
      return props.button as Record<string, unknown> | undefined;
    default:
      return undefined;
  }
}

/** Primary display ValueSource for supported primitive types. */
export function getDisplayValueSource(
  node: ComponentNode | CompiledRuntimeNode,
): ValueSource<unknown> | undefined {
  const block = typedBlock(node);
  if (!block) {
    return undefined;
  }
  if (node.type === 'button') {
    return unwrapValueSource(block.label);
  }
  // Dual-bind table: CTBL/fileTable on tableSource; list matrix on source.
  if (node.type === 'table') {
    return (
      unwrapValueSource(block.tableSource) ?? unwrapValueSource(block.source)
    );
  }
  // listView: display authority is the list payload; selection is separate.
  return unwrapValueSource(block.source);
}

/** Whether the component supports in-place editing that commits via setItemData. */
export function isComponentEditable(node: ComponentNode | CompiledRuntimeNode): boolean {
  const block = typedBlock(node);
  if (!block) {
    return false;
  }
  return block.editable === true;
}

/**
 * DataRef for readBindingByNodeId / write-back.
 * binding.read when present; else source.ref when editable and binding source.
 * listView writes the selection index (selectionSource), not the list payload.
 */
export function getPersistDataRef(
  node: ComponentNode | CompiledRuntimeNode,
): DataRef | undefined {
  const binding = node.binding;
  if (binding?.read && typeof binding.read === 'object' && 'kind' in binding.read) {
    return binding.read as DataRef;
  }
  if (!isComponentEditable(node)) {
    return undefined;
  }
  if (node.type === 'listView') {
    const block = typedBlock(node);
    return bindingRefFromValueSource(unwrapValueSource(block?.selectionSource));
  }
  const display = getDisplayValueSource(node);
  return bindingRefFromValueSource(display);
}

/** Invalid export: button.label wrapped as { source: ValueSource }. */
export function hasNestedButtonLabelSource(node: ComponentNode): boolean {
  if (node.type !== 'button') {
    return false;
  }
  const label = node.button?.label as unknown;
  if (label === null || typeof label !== 'object') {
    return false;
  }
  return !isValueSourceShape(label) && isValueSourceShape((label as { source?: unknown }).source);
}
